import React, { useState, useEffect, useCallback } from 'react'
import { motion } from 'framer-motion'
import {
  GitBranch,
  GitCommit,
  Tag,
  Calendar,
  User,
  FileText,
  ChevronLeft,
  ChevronRight,
  Clock,
  FolderOpen,
  X,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { AdminHeader } from '../../../shared/components'
import { apiService } from '../../../shared/services/api'
import { adminAuthService } from '../../../shared/services/adminAuth'
import MarkdownViewer from '../components/MarkdownViewer'

interface VersionInfo {
  version: string
  git: {
    branch: string
    hash: string
    tag: string
  }
}

interface GitCommitItem {
  hash: string
  shortHash: string
  author: string
  email: string
  date: string
  subject: string
}

interface WorkplanDocument {
  path: string
  fileName: string
  year: string
  month: string
  day: string
  title: string
  createdAt: string
}

interface Pagination {
  page: number
  limit: number
  total: number
  totalPages: number
}

const VersionManagementPage: React.FC = () => {
  const [versionInfo, setVersionInfo] = useState<VersionInfo | null>(null)
  const [commits, setCommits] = useState<GitCommitItem[]>([])
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  })
  const [documents, setDocuments] = useState<WorkplanDocument[]>([])
  const [selectedDoc, setSelectedDoc] = useState<WorkplanDocument | null>(null)
  const [docContent, setDocContent] = useState('')
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const [activeTab, setActiveTab] = useState<'git-log' | 'workplan'>('git-log')
  const [labelFilter, setLabelFilter] = useState<string | null>(null)

  // 커밋 메시지에서 prefix 레이블 추출
  const LABEL_COLORS: Record<string, { bg: string; text: string }> = {
    feat: {
      bg: 'bg-green-100 dark:bg-green-900/40',
      text: 'text-green-700 dark:text-green-300',
    },
    fix: {
      bg: 'bg-red-100 dark:bg-red-900/40',
      text: 'text-red-700 dark:text-red-300',
    },
    refactor: {
      bg: 'bg-purple-100 dark:bg-purple-900/40',
      text: 'text-purple-700 dark:text-purple-300',
    },
    chore: {
      bg: 'bg-gray-100 dark:bg-gray-700',
      text: 'text-gray-700 dark:text-gray-300',
    },
    docs: {
      bg: 'bg-blue-100 dark:bg-blue-900/40',
      text: 'text-blue-700 dark:text-blue-300',
    },
    style: {
      bg: 'bg-pink-100 dark:bg-pink-900/40',
      text: 'text-pink-700 dark:text-pink-300',
    },
    perf: {
      bg: 'bg-amber-100 dark:bg-amber-900/40',
      text: 'text-amber-700 dark:text-amber-300',
    },
    test: {
      bg: 'bg-cyan-100 dark:bg-cyan-900/40',
      text: 'text-cyan-700 dark:text-cyan-300',
    },
    ci: {
      bg: 'bg-orange-100 dark:bg-orange-900/40',
      text: 'text-orange-700 dark:text-orange-300',
    },
    build: {
      bg: 'bg-teal-100 dark:bg-teal-900/40',
      text: 'text-teal-700 dark:text-teal-300',
    },
  }

  const parseCommitLabel = (
    subject: string
  ): { label: string | null; message: string } => {
    const match = subject.match(/^([a-zA-Z]+)(?:\(.+?\))?!?:\s*(.*)$/)
    if (match) {
      const label = match[1].toLowerCase()
      return { label, message: match[2] }
    }
    return { label: null, message: subject }
  }

  // 필터링된 커밋
  const filteredCommits = labelFilter
    ? commits.filter(c => parseCommitLabel(c.subject).label === labelFilter)
    : commits

  // 현재 페이지의 레이블 통계
  const labelStats = commits.reduce(
    (acc, c) => {
      const { label } = parseCommitLabel(c.subject)
      const key = label || 'other'
      acc[key] = (acc[key] || 0) + 1
      return acc
    },
    {} as Record<string, number>
  )

  const getAuthHeaders = () => {
    const token = adminAuthService.getToken()
    return { Authorization: `Bearer ${token}` }
  }

  const loadVersionInfo = useCallback(async () => {
    try {
      const response = await apiService.client.get('/admin/version/info', {
        headers: getAuthHeaders(),
      })
      if (response.data.success) {
        setVersionInfo(response.data)
      }
    } catch (error) {
      console.error('Failed to load version info:', error)
    }
  }, [])

  const loadGitLog = useCallback(async (page = 1) => {
    try {
      const response = await apiService.client.get(
        `/admin/version/git-log?page=${page}&limit=20`,
        { headers: getAuthHeaders() }
      )
      if (response.data.success) {
        setCommits(response.data.commits)
        setPagination(response.data.pagination)
      }
    } catch (error) {
      console.error('Failed to load git log:', error)
      toast.error('Git 로그를 불러오지 못했습니다')
    }
  }, [])

  const loadWorkplanDocs = useCallback(async () => {
    try {
      const response = await apiService.client.get('/admin/version/workplan', {
        headers: getAuthHeaders(),
      })
      if (response.data.success) {
        setDocuments(response.data.documents)
      }
    } catch (error) {
      console.error('Failed to load workplan docs:', error)
    }
  }, [])

  const loadDocContent = async (doc: WorkplanDocument) => {
    try {
      const response = await apiService.client.get(
        `/admin/version/workplan/content?path=${encodeURIComponent(doc.path)}`,
        { headers: getAuthHeaders() }
      )
      if (response.data.success) {
        setSelectedDoc(doc)
        setDocContent(response.data.content)
      }
    } catch (error) {
      console.error('Failed to load document:', error)
      toast.error('문서를 불러오지 못했습니다')
    }
  }

  const loadAll = async () => {
    setLoading(true)
    try {
      await Promise.all([loadVersionInfo(), loadGitLog(1), loadWorkplanDocs()])
      setLastUpdated(new Date())
    } finally {
      setLoading(false)
    }
  }

  const handleRefresh = async () => {
    setRefreshing(true)
    try {
      await loadAll()
      toast.success('새로고침 완료')
    } finally {
      setRefreshing(false)
    }
  }

  useEffect(() => {
    loadAll()
  }, [])

  const handlePageChange = (newPage: number) => {
    loadGitLog(newPage)
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const formatRelativeDate = (dateStr: string) => {
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMin = Math.floor(diffMs / 60000)
    const diffHour = Math.floor(diffMs / 3600000)
    const diffDay = Math.floor(diffMs / 86400000)

    if (diffMin < 1) {
      return '방금 전'
    }
    if (diffMin < 60) {
      return `${diffMin}분 전`
    }
    if (diffHour < 24) {
      return `${diffHour}시간 전`
    }
    if (diffDay < 30) {
      return `${diffDay}일 전`
    }
    return formatDate(dateStr)
  }

  // 문서를 날짜별로 그룹핑
  const groupedDocs = documents.reduce(
    (acc, doc) => {
      const key = `${doc.year}-${doc.month}`
      if (!acc[key]) {
        acc[key] = []
      }
      acc[key].push(doc)
      return acc
    },
    {} as Record<string, WorkplanDocument[]>
  )

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <AdminHeader title="버전 관리" subtitle="Git 로그 및 작업 이력 관리" />
        <div className="flex h-96 items-center justify-center">
          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-indigo-600" />
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <AdminHeader
        title="버전 관리"
        subtitle="Git 로그 및 작업 이력 관리"
        showRefresh={true}
        onRefresh={handleRefresh}
        isRefreshing={refreshing}
        lastUpdated={lastUpdated}
      />

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* 버전 정보 카드 */}
        {versionInfo && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-4"
          >
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <div className="flex items-center space-x-3">
                <div className="rounded-lg bg-indigo-100 p-2 dark:bg-indigo-900/50">
                  <Tag className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    현재 버전
                  </p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    v{versionInfo.version}
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <div className="flex items-center space-x-3">
                <div className="rounded-lg bg-green-100 p-2 dark:bg-green-900/50">
                  <GitBranch className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    브랜치
                  </p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {versionInfo.git.branch}
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <div className="flex items-center space-x-3">
                <div className="rounded-lg bg-amber-100 p-2 dark:bg-amber-900/50">
                  <GitCommit className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    최신 커밋
                  </p>
                  <p className="font-mono text-xl font-bold text-gray-900 dark:text-white">
                    {versionInfo.git.hash}
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <div className="flex items-center space-x-3">
                <div className="rounded-lg bg-purple-100 p-2 dark:bg-purple-900/50">
                  <FileText className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    작업 문서
                  </p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {documents.length}건
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* 탭 네비게이션 */}
        <div className="mb-6 flex w-fit space-x-1 rounded-lg bg-gray-100 p-1 dark:bg-gray-800">
          <button
            onClick={() => setActiveTab('git-log')}
            className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'git-log'
                ? 'bg-white text-indigo-600 shadow-sm dark:bg-gray-700 dark:text-indigo-400'
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <GitCommit className="mr-2 inline-block h-4 w-4" />
            Git 로그
          </button>
          <button
            onClick={() => setActiveTab('workplan')}
            className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'workplan'
                ? 'bg-white text-indigo-600 shadow-sm dark:bg-gray-700 dark:text-indigo-400'
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <FolderOpen className="mr-2 inline-block h-4 w-4" />
            완료 작업 문서
          </button>
        </div>

        {/* Git 로그 탭 */}
        {activeTab === 'git-log' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800"
          >
            <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  커밋 이력
                </h2>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {labelFilter
                    ? `${filteredCommits.length}개 (${labelFilter})`
                    : `총 ${pagination.total}개 커밋`}
                </span>
              </div>
              {/* 레이블 필터 */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setLabelFilter(null)}
                  className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                    !labelFilter
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  전체
                </button>
                {Object.entries(labelStats)
                  .sort((a, b) => b[1] - a[1])
                  .map(([label, count]) => {
                    const colors = LABEL_COLORS[label] || {
                      bg: 'bg-gray-100 dark:bg-gray-700',
                      text: 'text-gray-600 dark:text-gray-300',
                    }
                    return (
                      <button
                        key={label}
                        onClick={() =>
                          setLabelFilter(labelFilter === label ? null : label)
                        }
                        className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                          labelFilter === label
                            ? 'ring-2 ring-indigo-500 ' +
                              colors.bg +
                              ' ' +
                              colors.text
                            : colors.bg +
                              ' ' +
                              colors.text +
                              ' hover:opacity-80'
                        }`}
                      >
                        {label} ({count})
                      </button>
                    )
                  })}
              </div>
            </div>

            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {filteredCommits.length === 0 ? (
                <div className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                  해당 레이블의 커밋이 없습니다
                </div>
              ) : (
                filteredCommits.map((commit, index) => {
                  const { label, message } = parseCommitLabel(commit.subject)
                  const colors = label
                    ? LABEL_COLORS[label] || {
                        bg: 'bg-gray-100 dark:bg-gray-700',
                        text: 'text-gray-600 dark:text-gray-300',
                      }
                    : null
                  return (
                    <motion.div
                      key={commit.hash}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.02 }}
                      className="px-6 py-4 transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    >
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="mb-1 flex items-center space-x-2">
                            <code className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-indigo-600 dark:bg-gray-700 dark:text-indigo-400">
                              {commit.shortHash}
                            </code>
                            {label && colors && (
                              <button
                                onClick={() =>
                                  setLabelFilter(
                                    labelFilter === label ? null : label
                                  )
                                }
                                className={`cursor-pointer rounded-full px-2 py-0.5 text-xs font-semibold transition-opacity hover:opacity-80 ${colors.bg} ${colors.text}`}
                              >
                                {label}
                              </button>
                            )}
                            <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
                              {message}
                            </p>
                          </div>
                          <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                            <span className="flex items-center">
                              <User className="mr-1 h-3 w-3" />
                              {commit.author}
                            </span>
                            <span className="flex items-center">
                              <Clock className="mr-1 h-3 w-3" />
                              {formatRelativeDate(commit.date)}
                            </span>
                            <span
                              className="hidden text-gray-400 dark:text-gray-500 sm:inline"
                              title={commit.date}
                            >
                              {formatDate(commit.date)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )
                })
              )}
            </div>

            {/* 페이지네이션 */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4 dark:border-gray-700">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  페이지 {pagination.page} / {pagination.totalPages}
                </p>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                    className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.totalPages}
                    className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* 완료 작업 문서 탭 */}
        {activeTab === 'workplan' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid grid-cols-1 gap-6 lg:grid-cols-3"
          >
            {/* 문서 목록 */}
            <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800 lg:col-span-1">
              <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  문서 목록
                </h2>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  총 {documents.length}건
                </p>
              </div>

              <div className="max-h-[600px] overflow-y-auto">
                {documents.length === 0 ? (
                  <div className="px-6 py-12 text-center">
                    <FileText className="mx-auto mb-3 h-12 w-12 text-gray-300 dark:text-gray-600" />
                    <p className="text-gray-500 dark:text-gray-400">
                      완료된 작업 문서가 없습니다
                    </p>
                  </div>
                ) : (
                  Object.entries(groupedDocs).map(([yearMonth, docs]) => (
                    <div key={yearMonth}>
                      <div className="dark:bg-gray-750 border-b border-gray-100 bg-gray-50 px-6 py-2 dark:border-gray-700">
                        <span className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
                          <Calendar className="mr-1 inline-block h-3 w-3" />
                          {yearMonth}
                        </span>
                      </div>
                      {docs.map(doc => (
                        <button
                          key={doc.path}
                          onClick={() => loadDocContent(doc)}
                          className={`w-full border-b border-gray-100 px-6 py-3 text-left transition-colors hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/50 ${
                            selectedDoc?.path === doc.path
                              ? 'border-l-4 border-l-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                              : ''
                          }`}
                        >
                          <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
                            {doc.title}
                          </p>
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            {doc.createdAt} &middot; {doc.fileName}
                          </p>
                        </button>
                      ))}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* 문서 뷰어 */}
            <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800 lg:col-span-2">
              {selectedDoc ? (
                <>
                  <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {selectedDoc.title}
                      </h2>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        {selectedDoc.path}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedDoc(null)
                        setDocContent('')
                      }}
                      className="rounded-md p-1 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <X className="h-5 w-5 text-gray-400" />
                    </button>
                  </div>
                  <div className="max-h-[600px] overflow-y-auto px-6 py-6">
                    <MarkdownViewer content={docContent} />
                  </div>
                </>
              ) : (
                <div className="flex h-96 items-center justify-center">
                  <div className="text-center">
                    <FileText className="mx-auto mb-4 h-16 w-16 text-gray-300 dark:text-gray-600" />
                    <p className="text-gray-500 dark:text-gray-400">
                      좌측 목록에서 문서를 선택하세요
                    </p>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  )
}

export default VersionManagementPage
